/**
 * Karr — Home. The page you land on after login.
 *
 * There are two of them, and which one you get is the point of the `ob` lane
 * (`docs/ONBOARDING.md`):
 *
 *   - **Simple** → `SimpleHome` — one page, a chronological "what your business
 *     did" feed (O6). No widgets, no second view to toggle into.
 *   - **Standard** → `StandardDashboard` — the Books dashboard: figures in
 *     the header and sheets (plainface-b, PLAIN_FACE_SPEC.md §4).
 *
 * The nudges above the fold are shared, so they live here: written once, they
 * can never end up on one Home and not the other.
 *
 * Both Homes are **lazy**, and that is not incidental. `router.tsx` imports this
 * file eagerly, so before ob-3 the whole dashboard — recharts included — sat in
 * the initial bundle for every user, including the shopkeeper on Simple who has
 * no chart to draw. Splitting them means each person downloads their own Home
 * and not the other one. The route's own `<Suspense>` (in `appLayoutRoute`)
 * covers the swap; the local one below keeps the nudges painted while it lands.
 *
 * @since 1.0.0
 * @since ob-3 — split into the two Homes; this file chooses between them.
 */
import { Suspense, lazy } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AppLayout } from '@/shared/layouts/AppLayout';
import { useAuth } from '@/shared/hooks/useAuth';
import { useExperienceLevel } from '@/shared/hooks/useExperienceLevel';
// fastphone-4: from their own files; the components barrel pulls the AI panel and the wizard.
import { ModulesNudge } from '@/shared/components/ModulesNudge';
import { NotificationFallbackBanner } from '@/shared/components/NotificationFallbackBanner';
import { ErpArrivalsLine } from '@/shared/components/ErpArrivalsLine';

// bundletrim: only an org with an accountant draws it, so it stays out of the first load.
const AccountantsDesk = lazy(() => import('@/shared/components/AccountantsDesk'));
const SimpleHome = lazy(() => import('./dashboard/SimpleHome'));
// panellock (19 Oct 2026): lazy, it draws only once an accountant has stopped
// acting; this paid for the side-panel lock in the first load (check:bundle).
const ConsultantEndedBanner = lazy(() => import('@/shared/components/ConsultantEndedBanner').then((m) => ({ default: m.ConsultantEndedBanner })));
const StandardDashboard = lazy(() => import('./dashboard/StandardDashboard'));

/** Same shape as either Home's own skeleton, so the swap doesn't jump. */
function HomeFallback() {
  return (
    <div className="animate-pulse space-y-6">
      <div>
        <div className="h-8 w-56 bg-stone-200 dark:bg-stone-700 rounded mb-2" />
        <div className="h-4 w-72 bg-stone-100 dark:bg-stone-800 rounded" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-white dark:bg-stone-800 rounded-lg border border-stone-200 dark:border-stone-700" />
        ))}
      </div>
      <div className="h-72 bg-white dark:bg-stone-800 rounded-lg border border-stone-200 dark:border-stone-700" />
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { currentOrg } = useAuth();
  const { level } = useExperienceLevel();
  const orgId = currentOrg?.id;
  // flow-1 (FLOW_SCREEN_SPEC.md §1.3, §9): in Flow the home has exactly five
  // kinds of thing, and the nudges are not one of them. Each became part of a
  // block the home already has: the desk is the footer desk line, Get Started
  // and the modules nudge and the month-end prompt are Needs you rows, and
  // Books checkup is a footer line that speaks only when something is off.
  // Books keeps today's order (nudges above the widget dashboard).
  const isFlow = level === 'business_owner';

  const nudges = (
    <>
      {/* ── of-8: what your accountant is doing for you, right now, live.
           Self-hides when there is nothing outstanding, and when of-8's
           migration has not been applied. Shows stage only — never who. ── */}
      <div className="mb-4">
        <Suspense fallback={null}><AccountantsDesk orgId={orgId} /></Suspense>
      </div>

      {/* fillrail: the Get Started nudge is rows in the fill rail now, one
           place for everything still to do, instead of a card here too. */}

      {/* ── One-time modules nudge (mod-4) — self-hides once the org picks a
           module set or keeps everything. Legacy orgs that never interact see
           nothing here. ── */}
      <ModulesNudge />

      {/* ga-3's month-end nudge retired in flow-6: it is a Needs you row
          ("September is not closed yet"), which opens the same cockpit. */}

      {/* plainface-b: bh-2's Books Health badge is the dashboard's "Books health"
          figure now; the badge stays for the other pages that draw it. */}
    </>
  );

  return (
    <AppLayout currentRoute="/" orgName={currentOrg?.name || 'My Organization'}>
      {/* The two alerts stay above everything in both views; in Flow they are
          capped to the home's own column. */}
      <div className={isFlow ? 'mx-auto max-w-6xl' : undefined}>
        {/* ── notif-4: nobody is set to receive filing reminders, so they're going to
             the letterhead contact. Self-hides in the normal case; asks the real
             resolver, so it can't disagree with what the senders do. An ALERT, so
             it stays above the fold in both views. ── */}
        <NotificationFallbackBanner
          className="mb-4"
          // back-ok: Home is the first paint, kept free of the Back helper (bundle budget); Home is one press away
          onChoose={() => navigate({ to: '/settings', hash: 'notifications' })}
        />

        {/* ── dis-3: somebody has stopped acting for this business, and the
             business is run from THIS screen, not from the inbox the letter went
             to. Names the firm, the date, and the next thing falling due, which
             is the line that stops a return being missed by both sides at once.
             Self-hides when there is nothing to say. ── */}
        <Suspense fallback={null}><ConsultantEndedBanner className="mb-4" /></Suspense>
      </div>

      {/* ── erpin: the month's documents from the client's own ERP, and how
           many need a fix. Self-hides when no ERP sends here. ── */}
      <ErpArrivalsLine className="mb-4" />

      {!isFlow && nudges}

      <Suspense fallback={<HomeFallback />}>
        {isFlow ? <SimpleHome /> : <StandardDashboard />}
      </Suspense>
    </AppLayout>
  );
}

export default Dashboard;
